import React, { useState } from 'react'
import { Typography, Box, Theme, Chip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ClearIcon from '@material-ui/icons/Clear'
import { useRouter } from 'next/router'
import { FormattedMessage } from 'react-intl'
import AnalytesModal from './components/SelectAnalytesModal'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../Milliplex'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import clsx from 'clsx'

const useStyle = makeStyles((theme: Theme) => {
  return {
    part2HeaderStyle: {
      marginTop: theme.spacing(6),
    },
    analytesHeaderStyle: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(4),
    },
    container: {
      minHeight: theme.typography.pxToRem(86),
      padding: theme.spacing(0, 4, 6),
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightBold,
      '& > *': {
        marginRight: theme.spacing(4),
        marginTop: theme.spacing(6),
      },
    },
    analytesBorder: {
      border: `1px solid ${theme.palette.grey['500']}`,
    },
    analyteSingle: {
      marginRight: theme.spacing(4),
      marginTop: theme.spacing(6),
      background: theme.palette.primary.light,
      height: theme.spacing(9),
      borderRadius: theme.spacing(5),
      '& > $deleteIcon': {
        marginRight: theme.spacing(4),
      },
    },
    addAnalytes: {
      background: theme.palette.background.default,
      borderWidth: theme.typography.pxToRem(2),
      height: theme.spacing(9),
      borderRadius: theme.spacing(5),
      '& > $addAnalytesIcon': {
        marginLeft: theme.spacing(4),
      },
    },
    addAnalytesDisabled: {
      borderColor: theme.palette.grey['500'],
    },
    selectMessage: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightRegular,
      marginRight: theme.spacing(4),
    },
    addAnalytesIcon: {},
    deleteIcon: {
      width: theme.spacing(5),
    },
    addAnalytesLabel: {
      paddingLeft: theme.spacing(4),
    },
  }
})

export interface AnalytesProps {
  displayPageError: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
}

const Analytes: React.FC<AnalytesProps> = ({
  displayPageError,
  setDisplayPageError,
}) => {
  const classes = useStyle()
  const router = useRouter()
  const formik = useFormikContext<MilliplexFormValues>()
  const [openAddAnalytesModal, setOpenAddAnalytesModal] =
    useState<boolean>(false)

  // update based on the selection
  const handleDelete = (analyte: string) => {
    router.push(
      {
        query: {
          ...router.query,
          analytes: formik.values.analytes.filter(
            (single: string) => single !== analyte
          ),
        },
      },
      undefined,
      { scroll: false }
    )
    formik.setFieldValue(
      'analytes',
      formik.values.analytes.filter((single: string) => single !== analyte)
    )
    formik.setFieldTouched('analytes', false)
  }
  const toggleAnalytesModal = () => {
    setOpenAddAnalytesModal((prevState) => !prevState)
  }
  const disabled =
    formik.values.species === '' ||
    formik.values.plateType === '' ||
    formik.values.instrumentType.length === 0
  const addAnalytesButton = () => (
    <Chip
      classes={{
        root: classes.addAnalytes,
        icon: classes.addAnalytesIcon,
        disabled: disabled ? classes.addAnalytesDisabled : undefined,
      }}
      icon={<AddIcon />}
      label="Add Analytes"
      clickable
      color={disabled ? 'default' : 'primary'}
      onClick={toggleAnalytesModal}
      variant="outlined"
      disabled={disabled}
    />
  )

  return (
    <>
      <Typography variant="h3" className={classes.part2HeaderStyle}>
        <FormattedMessage id="PC_ANALYTE_milliplex" defaultMessage="Analytes" />
      </Typography>
      <Typography variant="subtitle2" className={classes.analytesHeaderStyle}>
        <FormattedMessage
          id="PC_ANALYTES_milliplex"
          defaultMessage="Select Analytes"
        />
      </Typography>
      <Box
        className={
          formik.values.analytes.length === 0
            ? clsx(classes.analytesBorder, classes.container)
            : classes.container
        }
      >
        {formik.values.analytes.length > 0 ? (
          <>
            {formik.values.analytes.map((analyte) => (
              <Chip
                classes={{
                  root: classes.analyteSingle,
                  deleteIcon: classes.deleteIcon,
                  label: classes.addAnalytesLabel,
                }}
                label={analyte}
                key={analyte}
                onDelete={() => handleDelete(analyte)}
                color="primary"
                variant="outlined"
                deleteIcon={<ClearIcon />}
              />
            ))}
            {addAnalytesButton()}
          </>
        ) : (
          <Box>
            <span className={classes.selectMessage}>
              <FormattedMessage
                id="MILLIPLEX_SELECT_ANALYTES_MESSAGE_UPDATED"
                defaultMessage="Please choose a single species, and then add the analytes for your search."
              />
            </span>

            {addAnalytesButton()}
          </Box>
        )}
      </Box>
      <ValidationErrors
        errors={
          formik.touched.analytes && formik.errors.analytes
            ? Array.isArray(formik.errors.analytes)
              ? formik.errors.analytes
              : [formik.errors.analytes]
            : []
        }
        configuratorsRedesign
      />

      <AnalytesModal
        open={openAddAnalytesModal}
        handleClose={toggleAnalytesModal}
        displayPageError={displayPageError}
        setDisplayPageError={setDisplayPageError}
      />
    </>
  )
}

export default Analytes
